"use client"

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  ArrowLeft, 
  Search,
  Building,
  Briefcase,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Clock
} from 'lucide-react'
import { get } from '../../utils/service'

interface Lead {
  id: string
  company_name: string
  contact_person: string
  email: string
  phone: string
  source: string
  urgency: string
  budget_range: string
  type: string
  current_stage: number
  status: string
  progress: string
  value: number
  location: string
  created_by: string
  created_at: string
  updated_at: string
  first_name: string
  last_name: string
  result_type: 'lead'
}

interface Project {
  id: string
  lead_id: string | null
  name: string
  client: string
  client_email: string
  client_phone: string
  type: string
  value: number
  current_stage: number
  status: string
  progress: string
  location: string
  created_by: string
  created_at: string
  updated_at: string
  first_name: string
  last_name: string
  result_type: 'project'
}

interface Activity {
  id: string
  user_id: string
  action: string
  description: string
  entity_type: string
  entity_id: string
  created_at: string
  first_name: string
  last_name: string
  result_type: 'activity'
}

type SearchResult = Lead | Project | Activity

interface SearchResponse {
  success: boolean
  message: string
  data: SearchResult[]
  pagination: {
    total: number
    count: number
    per_page: number
    current_page: number
    total_pages: number
    has_more: boolean
  }
}

const SearchResultsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''
  
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalResults, setTotalResults] = useState(0)

  useEffect(() => {
    if (query) {
      fetchSearchResults()
    } else {
      setLoading(false)
    }
  }, [query])

  const fetchSearchResults = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await get<SearchResponse>(
        `/search?q=${encodeURIComponent(query)}&type=all&page=1&page_size=20`
      )
      
      if (response.success) {
        setResults(response.data)
        setTotalResults(response.pagination.total)
      } else {
        setError('No results found')
      }
    } catch (err) {
      setError('Failed to load search results')
      console.error('Search error:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'qualified':
        return 'text-green-600'
      case 'pending':
      case 'contacted':
        return 'text-orange-600'
      case 'overdue':
      case 'lost':
        return 'text-red-600'
      case 'new':
        return 'text-blue-600'
      default:
        return 'text-gray-600'
    }
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency.toLowerCase()) {
      case 'high':
        return 'text-red-600'
      case 'medium':
        return 'text-orange-600'
      case 'low':
        return 'text-green-600'
      default:
        return 'text-gray-600'
    }
  }

  const SkeletonCard = () => (
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="flex items-center gap-4 mb-2">
        <div className="h-3 bg-gray-200 rounded w-24"></div>
        <div className="h-3 bg-gray-200 rounded w-20"></div>
      </div>
      <div className="h-3 bg-gray-200 rounded w-32"></div>
    </div>
  )

  const renderLead = (lead: Lead) => (
    <motion.div
      key={lead.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="pb-4 mb-4 border-b border-gray-100 last:border-0 last:mb-0 cursor-pointer"
      onClick={() => navigate(`/leads/${lead.id}`)}
    >
      <div className="flex items-start gap-3 mb-2">
        <Building className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-900 mb-1">{lead.company_name}</h3>
          <p className="text-xs text-gray-600 mb-2">{lead.contact_person}</p>
          
          <div className="flex items-center gap-4 mb-2 flex-wrap">
            <span className={`text-xs font-medium ${getStatusColor(lead.status)}`}>
              {lead.status.replace('_', ' ')}
            </span>
            <span className={`text-xs font-medium ${getUrgencyColor(lead.urgency)}`}>
              {lead.urgency} priority
            </span>
            <span className="text-xs text-gray-600">
              {lead.type.replace('_', ' ')}
            </span>
          </div>

          <div className="flex items-center gap-4 mb-2 text-xs text-gray-600 flex-wrap">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {lead.location}
            </div>
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {lead.phone}
            </div>
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {lead.email}
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Stage {lead.current_stage}</span>
            <span>•</span>
            <span>{lead.progress}% complete</span>
            <span>•</span>
            <span>{formatCurrency(lead.value)}</span>
            <span>•</span>
            <span>{formatDate(lead.created_at)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  )

  const renderProject = (project: Project) => (
    <motion.div
      key={project.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="pb-4 mb-4 border-b border-gray-100 last:border-0 last:mb-0 cursor-pointer"
      onClick={() => navigate(`/projects/${project.id}`)}
    >
      <div className="flex items-start gap-3 mb-2">
        <Briefcase className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-900 mb-1">{project.name}</h3>
          <p className="text-xs text-gray-600 mb-2">{project.client}</p>
          
          <div className="flex items-center gap-4 mb-2 flex-wrap">
            <span className={`text-xs font-medium ${getStatusColor(project.status)}`}>
              {project.status.replace('_', ' ')}
            </span>
            <span className="text-xs text-gray-600">
              {project.type.replace('_', ' ')}
            </span>
          </div>

          <div className="flex items-center gap-4 mb-2 text-xs text-gray-600 flex-wrap">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {project.location}
            </div>
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {project.client_phone}
            </div>
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {project.client_email}
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Stage {project.current_stage}</span>
            <span>•</span>
            <span>{project.progress}% complete</span>
            <span>•</span>
            <span>{formatCurrency(project.value)}</span>
            <span>•</span>
            <span>{formatDate(project.created_at)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  )

  const renderActivity = (activity: Activity) => (
    <motion.div
      key={activity.id}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="pb-4 mb-4 border-b border-gray-100 last:border-0 last:mb-0"
    >
      <div className="flex items-start gap-3 mb-2">
        <Clock className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-xs font-semibold text-gray-900 mb-1">{activity.description}</h3>
          
          <div className="flex items-center gap-4 mb-2 flex-wrap">
            <span className="text-xs text-gray-600 font-medium">
              {activity.action.replace('_', ' ')}
            </span>
            <span className="text-xs text-gray-600">
              {activity.entity_type}
            </span>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {activity.first_name} {activity.last_name}
            </div>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(activity.created_at)}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )

  const renderResult = (result: SearchResult) => {
    switch (result.result_type) {
      case 'lead':
        return renderLead(result as Lead)
      case 'project':
        return renderProject(result as Project)
      case 'activity':
        return renderActivity(result as Activity)
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors text-xs"
          >
            <ArrowLeft className="w-3 h-3" />
            Back
          </button>

          <div className="flex items-center gap-3 mb-2">
            <Search className="w-4 h-4 text-gray-400" />
            <h1 className="text-sm font-semibold text-gray-900">Search Results</h1>
          </div>
          
          <p className="text-xs text-gray-600">
            {loading ? 'Searching...' : `${totalResults} result${totalResults !== 1 ? 's' : ''} for`}
            {!loading && <span className="font-medium text-gray-900"> "{query}"</span>}
          </p>
        </div>

        {/* Results */}
        <div className="space-y-0">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-600">{error}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-600">
                No results found for "<span className="font-medium">{query}</span>"
              </p>
            </div>
          ) : (
            <div className="space-y-0">
              {results.map(renderResult)}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchResultsPage